/**
 * ProUpgradeBanner - Context-aware upgrade prompt for Pro subscription.
 * Props: context ('chat' | 'diagnosis' | 'general'), lang ('ja' | 'en')
 */

import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';
import billingService from '../../services/billingService';

const Container = styled.div`
  max-width: 480px;
  margin: 0 auto;
  padding: 32px 24px;
  text-align: center;
`;

const IconCircle = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(99, 102, 241, 0.1);
  margin-bottom: 16px;
  font-size: 1.8rem;
`;

const Title = styled.h2`
  font-size: 1.2rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text};
  margin: 0 0 8px 0;
`;

const Description = styled.p`
  font-size: 0.88rem;
  color: ${(p) => p.theme.colors.textSecondary};
  margin: 0 0 24px 0;
  line-height: 1.6;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  @media (min-width: 480px) {
    flex-direction: row;
    justify-content: center;
  }
`;

const PrimaryButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: ${(p) => p.theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: ${(p) => p.theme.borderRadius.md};
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover { opacity: 0.85; }
`;

const CancelNote = styled.p`
  font-size: 0.75rem;
  color: ${(p) => p.theme.colors.textMuted};
  margin-top: 16px;
`;

const MESSAGES = {
  chat: {
    ja: { title: 'AIチャットはProプラン限定です', desc: 'Proプランに加入すると、AIチャットで自分の内面構造について深く対話できます。' },
    en: { title: 'AI Chat requires Pro', desc: 'Subscribe to Pro to unlock unlimited AI chat about your personality structure.' },
  },
  diagnosis: {
    ja: { title: '再診断はProプラン限定です', desc: 'Proプランに加入すると、何度でも診断をやり直すことができます。' },
    en: { title: 'Re-diagnosis requires Pro', desc: 'Subscribe to Pro to retake the diagnosis anytime.' },
  },
  general: {
    ja: { title: 'Proプランでもっと深く', desc: '全レポート閲覧 + AIチャット無制限 + 再診断が利用可能になります。' },
    en: { title: 'Go deeper with Pro', desc: 'Unlock all reports, unlimited AI chat, and re-diagnosis.' },
  },
};

function ProUpgradeBanner({ context = 'general' }) {
  const { t } = useLanguage();

  const msgs = MESSAGES[context] || MESSAGES.general;

  const handleCheckout = async (planType) => {
    try {
      const { checkout_url } = await billingService.createIndividualCheckout(planType);
      window.location.href = checkout_url;
    } catch (err) {
      alert(err.message || 'Failed to create checkout');
    }
  };

  return (
    <Container>
      <IconCircle>Pro</IconCircle>
      <Title>{t(msgs.ja.title, msgs.en.title)}</Title>
      <Description>{t(msgs.ja.desc, msgs.en.desc)}</Description>
      <ButtonGroup>
        <PrimaryButton onClick={() => handleCheckout('monthly')}>
          {t('月額 ¥980', '¥980/month')}
        </PrimaryButton>
      </ButtonGroup>
      <CancelNote>{t('いつでもキャンセル可能', 'Cancel anytime')}</CancelNote>
    </Container>
  );
}

export default ProUpgradeBanner;
